<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { useClaimStore, useNotificationStore } from '../../store';
import { CogIcon, ArrowTopRightOnSquareIcon, EnvelopeIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/solid';

import adminAPI from '../../services/api';
import { isValidEmail } from "@/utils";
import openBoxFolderForCurrentClaim, { openBoxSharedFolderForCurrentClaim } from "../../utils/open-box-folder";
import DotLegalStep from '../ui/badges/DotLegalStep.vue';
import { storeToRefs } from 'pinia';
import { computedAsync } from '@vueuse/core';
import BoxFilePicker from '../Box/BoxFilePicker.vue';
import { AtSign, FileSignatureIcon } from 'lucide-vue-next';
import BaseInput from '../ui/inputs/BaseInput.vue';
import BaseButton from '../ui/buttons/BaseButton.vue';
import { BadgeCheckIcon } from 'lucide-vue-next';
import LoaderButton from '../ui/loaders/LoaderButton.vue';
import { InfoIcon } from 'lucide-vue-next';
import Alert from '@/components/ui/alerts/Alert.vue';
import { groupBy } from 'ramda';
import { Model48, ReceivableContractsStatus } from '@/interfaces/data-contracts';

const claimId = computed(() => useClaimStore().claim.id);
const claim = storeToRefs(useClaimStore()).claim;

/** Email to notify */
async function getTravelAgencyEmail() {
  const feedback = claim.value.agentFeedback;
  const emails = await adminAPI.retrieveClaimContactEmails(claimId.value);
  const isBusinessTravel = claim.value.legalSpecification.impactedSegment.reservation.travelAgencyCompany
    || feedback?.b2c === false
    || !feedback && claim.value.legalSpecification.impactedSegment.reservation.travelAgencyOffice.isBusinessTravel

  if (emails) {
    if (!isBusinessTravel) {
      return emails.reservationAgentEmail || emails.fallbackOfficeEmail;
    } else {
      return emails.accountManagerEmail  || emails.fallbackOfficeEmail;
    }
  }

  return null;
}
const email = ref(await getTravelAgencyEmail());
watch(claimId, async () => email.value = await getTravelAgencyEmail());

/** Get PNR contacts */

const contacts = ref();
watch(claimId, async () => {
  contacts.value = await adminAPI.retrievePnrContacts(claimId.value)
}, { immediate: true });

const isBusinessTravel = computed(() => Boolean(
  claim.value.legalSpecification.impactedSegment.reservation.travelAgencyCompany
    || claim.value.agentFeedback?.b2c === false
));
const isDirectSignatureAllowed = computed(() => {
  if (isBusinessTravel.value) {
    return claim.value.legalSpecification.impactedSegment.reservation.travelAgencyCompany?.isDirectPassengerContactAllowed
  } else {
    return true
  }
})

const evaluating = ref(true);
const receivables = ref([] as ReceivableContractsStatus); // @todo interface naming!!
watch(claimId, async () => {
  evaluating.value = true;
  receivables.value = await adminAPI.listReceivableContracts(claimId.value);
  evaluating.value = false;
}, { immediate: true })

const signatureForm = ref<any>({});

// const selectedFiles = ref<any[]>([]);
// function handleChoose(files) {
//   selectedFiles.value = files;
// }

async function generateReceivables() {
  const loadingNotification = useNotificationStore().emitNotification({
    type: "wait",
    title: "Document en cours de génération (~30 sec à 1min)...",
    
  }, 90000);
  const claimId = useClaimStore().claim?.id;
  const documentType = "receivable-contract";
  const response = await adminAPI.instance.post(
    `/admin/claims/${claimId}/documents/${documentType}`,
    null,
    { responseType: "blob" }
  );
  useNotificationStore().emitNotification({
    type: "success",
    title: "Documents générés avec succès"
  })
  useClaimStore().retrieveClaim(useClaimStore().claim.id);
  loadingNotification.duration = 0;
  receivables.value = await adminAPI.listReceivableContracts(useClaimStore().claim.id);
  // downloadFileFromResponse(response);
}

async function sendSignature(fileId: string) {
  const waitNotification = useNotificationStore().emitNotification({
    title: "Préparation de la signature en cours",
    type: "wait",
  });
  signatureForm.value[fileId].isLoading = true;
  try {
    // signatureForm.value[fileId].result = await adminAPI.sendSignatureRequest(fileId, {
    //   signerEmail: signatureForm.value[fileId].email,
    //   claimId: useClaimStore().claim.id,
    // });

    await adminAPI.launchFamilyAcquisitionWorkflow({
      claimId: useClaimStore().claim.id,
      familyId: signatureForm.value[fileId].familyId,
      signerFullName: signatureForm.value[fileId].signerFullName,
      signerEmail: signatureForm.value[fileId].email,
      boxFileId: fileId,
    })

    console.log("signatureForm.value[fileId].result", signatureForm.value[fileId].result)
    evaluating.value = true;
    receivables.value = await adminAPI.listReceivableContracts(claimId.value);
    evaluating.value = false;
    await useClaimStore().refreshClaim();
    useNotificationStore().emitNotification({
      title: "Signature créée avec succès"
    });

  } catch (error) {
    useNotificationStore().emitNotification({
      title: "Une erreur est survenue lors de la création de la signature",
      type: "error",
    });
  } finally {
    waitNotification.duration = 0
    signatureForm.value[fileId].isLoading = false;
  }
}

const items = ref([
  {
    name: 'Générer les cessions',
    description: 'Vérifier les montants MC99. Une fois générés, les document sont accessibles sur Box.',
    iconColor: 'bg-purple-500',
    cta: {
      text: 'Générer',
      handleClick: generateReceivables,
      tooltip: useClaimStore().claim?.boxFolderIdGeneratedRecevables
        ? "Les cessions ont déjà été générées"
        : null
    },
    icon: CogIcon,
  },
    {
    name: 'Vérifier les cessions',
    description: 'Une fois générés, les document sont accessibles sur Box.',
    iconColor: 'bg-purple-500',
    cta: {
      text: 'Vérifier',
      handleClick: () => openBoxSharedFolderForCurrentClaim(),
    },
    icon: ArrowTopRightOnSquareIcon,
  },
  {
    name: 'Transmettre les cessions à l\'agence',
    description: `Email à l\'agent ou au chargé de compte avec les documents à faire signer.`,
    iconColor: 'bg-yellow-500',
    cta: {
      text: 'Envoyer',
      handleClick: async () => {
        const loadingNotification = useNotificationStore().emitNotification({
          type: "wait",
          title: "Envoie à " + email.value + "...",
          
        }, 15000);
        const claimId = useClaimStore().claim?.id;

        const apiResponse = await adminAPI.validation_notifyReceivablesContractsReady(claimId, {
          email: email.value as string
        });
        if (apiResponse?.success) {
          loadingNotification.duration = 0
          useNotificationStore().emitNotification({
            type: "success",
            title: "Notification envoyée par mail à " + email.value
          })
          useClaimStore().retrieveClaim(useClaimStore().claim.id);
        }
      },
      tooltip: null,
    },
    icon: EnvelopeIcon,
  },
]);

const showSubmitNoteButton = ref(false);
async function saveCustomerNote() {
  const { success } = await adminAPI.saveCustomerNote(claimId.value, {
    passengersQuestion: useClaimStore().claim.passengersQuestion,
  });
}
watch(() => useClaimStore().claim.passengersQuestion, () => showSubmitNoteButton.value = true);
watch(() => useClaimStore().claim.id, () => showSubmitNoteButton.value = false, { immediate: true });
</script>
<template>
<div class="border-b bg-slate-50">
  <div class="max-w-lg py-5 mx-auto text-left">
    <div class="p-8 bg-white border rounded-md shadow">
      <h2 class="inline-flex text-base font-semibold leading-6 text-gray-900 align-middle">
        <!-- <div class="flex-none p-0.5 bg-red-400/30 rounded-full"><div class="h-1.5 w-1.5 rounded-full bg-red-500"></div></div> -->
        <div class="py-1 mr-2"><DotLegalStep :legal-step-name="useClaimStore().claim.claimLegalStep.name" /></div>
        Actions requises
      </h2>
      <p class="mt-1 text-sm text-gray-500">Génération, vérification et envoie des cessions de créances.</p>
      <div v-permission="['UPCLAIM_ADMIN']" class="pt-6">
        <Alert
          v-if="!isDirectSignatureAllowed"
          title="Contact direct des voyageurs non autorisé"
          :message="String().concat(isBusinessTravel ? 'Le chargé de comptes' : 'L\'agent', ' s’occupe de transmettre et d’obtenir les signatures.', `<br><br> Email envoyé à « <b>${email}</b> »`)"
          type="warning"
          :with-border="false"
        />
        <Alert
          v-else
          title="Contact direct des voyageurs autorisé"
          :message="`Ou envoie pour signature manuelle à «<u>${email}</u>»`"
          type="info"
          :with-border="true"
        />
      </div>
      <div class="w-full py-6">
        <BaseInput label="Adresse pour envoi manuel" :value="email" v-model="email" :icon="AtSign" class="mx-auto" />
      </div>
      <ul role="list" class="mt-6 border-t border-b border-gray-200 divide-y divide-gray-200">
        <li v-for="(item, itemIdx) in items" :key="itemIdx">
          <div class="relative flex items-start py-4 space-x-6 pointer-events-auto group">
            <div v-if="false" class="flex-shrink-0">
              <span :class="[item.iconColor, 'inline-flex h-10 w-10 items-center justify-center rounded-lg']">
                <component :is="item.icon" class="w-6 h-6 text-white" aria-hidden="true" />
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-900">
                <p>
                  <!-- <span class="absolute inset-0" aria-hidden="true" /> -->
                  {{ itemIdx + 1 }}. {{ item.name }}
                </p>
              </div>
              <div class="text-sm text-gray-500">
                <p v-html="item.description" />
              </div>
            </div>
            <div class="self-center flex-shrink-0">
              <button type="button" v-tooltip="item.cta.tooltip"
                @click="() => item.cta.handleClick()"
                class="inline-flex cursor-pointer px-2 items-center gap-x-1.5 py-1.5 text-sm font-semibold text-gray-700 bg-white rounded shadow-sm group-hover:text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                <component :is="item.icon" class="w-4 h-4 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                {{ item.cta.text }}
              </button>
              
              <!-- <ChevronRightIcon class="w-5 h-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" /> -->
            </div>
          </div>
        </li>
      </ul>

      <div v-if="useClaimStore().claim.boxFolderIdGeneratedRecevables" class="relative block mt-6 text-sm">
        <h3 class="mb-2 font-medium leading-7 text-gray-900">Demandes de signatures</h3>

        <div class="block px-2 mt-8">
        <p class="inline-flex items-center text-sm font-medium leading-6 text-gray-700">
          <span class="inline-flex">
            <QuestionMarkCircleIcon class="flex-shrink-0 w-5 h-5 mr-2 text-gray-400" />
            Question à destination des passagers ?
          </span>
        </p>
        <div class="flex flex-col items-end mt-2 text-sm text-gray-900">
          <textarea :readonly="false" @update:modelValue="showSubmitNoteButton = true" v-model="useClaimStore().claim.passengersQuestion" :rows="2" name="passengers-question" id="passengers-question" class="mb-5 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" />
          <BaseButton
            @click="saveCustomerNote"
            v-if="showSubmitNoteButton"
            v-permission="['UPCLAIM_ADMIN']"
            type="submit"
            text="Sauvergarder"
          />
        </div>
      </div>
        <div class="py-6 my-3 mt-4 border rounded">
          <div v-if="!receivables.length" class="text-center">
            <LoaderButton v-if="evaluating" class="mx-auto" />
            <p v-else class="inline-flex items-center">
              <span class="mr-2 text-gray-500"><InfoIcon :size="16" /></span>
              Aucune cession de créance passager détectée
            </p>
          </div>
          <div v-if="receivables?.length">
            <ul v-if="contacts?.length" class="px-4 py-5 mb-6 prose list-item">
              <li v-for="contact of contacts" class="ml-8 list-disc">
                {{ [((contact.firstName || '') + ' ' + (contact.lastName || '')).trim(), contact.email, contact.phone].filter(Boolean).join(" / ") }}
                <!-- {{ contact.firstName }} {{ contact.firstName }} <span v-if="contact.email">/ {{ contact.email }}</span> <span v-if="contact.phone"> / {{ contact.phone }}</span> -->
              </li>
            </ul>
          </div>
          <div v-for="receivable of receivables" :key="receivable.boxFileId">
            <div class="inline-flex align-middle">
              <span class="px-4"><FileSignatureIcon /></span>
              <div class="block leading-6">
                <p>Cession « <a :href="`https://upclaim.app.box.com/file/${receivable.boxFileId}`" target="_blank" class="link">{{ receivable.boxFileName }}</a> »</p>
                <p>Passager(s) : <span>{{ receivable.passengers.map(({ fullName }) => fullName).join(", ") }}</span></p>
                <div v-if="!receivable.signatureRequest?.createdAt">
                  <BaseInput
                    placeholder="Adresse email"
                    @update:model-value="(value) => {
                      signatureForm[receivable.boxFileId] = {
                        email: value,
                        fileId: receivable.boxFileId,
                        familyId: receivable.passengers[0].familyId,
                        signerFullName: signatureForm[receivable.boxFileId]?.signerFullName
                          || receivable.passengers[0].fullName
                      }
                    }"
                  />
                  <BaseInput
                    placeholder="Nom complet du signataire"
                    @update:model-value="(value) => {
                      signatureForm[receivable.boxFileId] = {
                        ...signatureForm[receivable.boxFileId],
                        signerFullName: value,
                      }
                    }"
                  />
                </div> 
                <div v-else class="inline-flex items-center mt-2 text-sm italic text-gray-500">
                  <span class="mr-2 text-green-700"><BadgeCheckIcon :size="16" /></span>
                  Signature envoyée il y a {{ formatDistanceToNow(new Date(receivable.signatureRequest.createdAt), { locale: fr }) }}
                </div>
              </div>
            </div>
            <div class="inline-flex mt-3 px-14" v-if="!receivable.signatureRequest?.createdAt">
              <BaseButton text="Envoyer la signature"
                @click="sendSignature(receivable.boxFileId)"
                :disabled="!isValidEmail(signatureForm[receivable.boxFileId]?.email)"
                :loading="signatureForm[receivable.boxFileId]?.isLoading"
              />
            </div>
            <!-- <div v-else class="mt-3 px-14">
              <span
                class="inline-flex border items-center justify-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              >
                {{ receivable.signatureRequest.step }}
              </span>
            </div> -->
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>